import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// 90s timeout — long enough for slow Claude calls, short enough to surface
// real failures rather than letting axios hang silently.
export const api = axios.create({ baseURL: API, timeout: 90000 });

// ---------------------------------------------------------------------------
// Iter 31.155 — RequestRateGuard: client-side runaway circuit breaker.
//
// WHY: On 30 May 2026 a Shell useEffect dep-array bug spun a tight loop
// of GET /credits/balance + /payments/me/entitlements — 337 requests in
// 49 seconds before Six spotted it in DevTools. Iter 31.154 fixed that
// specific bug, but we want a permanent guard so the *next* runaway
// can never reach production unseen.
//
// HOW: Per-endpoint sliding-window counter. If any endpoint fires
// THRESHOLD+ times within WINDOW_MS, we:
//   1. Log a loud console.error.
//   2. Dispatch `shed:runaway` event with {endpoint, count, window_ms}
//      so the RunawayBanner component can scream visibly.
//   3. Reject all further requests to that endpoint for COOLDOWN_MS,
//      hard-stopping the loop at the network layer (React's catch
//      branch fires instead of setState → loop dies).
// After cooldown the slate is wiped clean and traffic resumes.
//
// Thresholds chosen so no legitimate UI flow can trip them: 8 calls
// to the SAME path in 2 seconds is unambiguously a bug.
// ---------------------------------------------------------------------------
const RUNAWAY_WINDOW_MS = 2_000;
const RUNAWAY_THRESHOLD = 8;
const RUNAWAY_COOLDOWN_MS = 5_000;
const _rate_hits = new Map();      // path -> [timestamps]
const _rate_blocked = new Map();   // path -> blocked-until timestamp

function _normalizePath(config) {
    // Strip baseURL and query string so /credits/balance?x=1 buckets
    // with /credits/balance. axios prepends baseURL only if the url is
    // relative — handle both shapes.
    try {
        const raw = (config?.url || "").split("?")[0];
        if (raw.startsWith("http")) {
            const u = new URL(raw);
            return u.pathname;
        }
        return raw.startsWith("/") ? raw : `/${raw}`;
    } catch (_) {
        return config?.url || "";
    }
}

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("msd_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // --- Runaway guard ---
    const path = _normalizePath(config);
    if (path) {
        const now = Date.now();
        const blockedUntil = _rate_blocked.get(path) || 0;
        if (now < blockedUntil) {
            // Endpoint is on cooldown — reject without firing the request.
            const remainingMs = blockedUntil - now;
            const err = new Error(
                `RunawayGuard: ${path} is throttled for ${Math.ceil(remainingMs / 1000)}s ` +
                `after firing too fast. The calling component has a useEffect ` +
                `dep-array bug or a tight polling loop.`
            );
            err.isRunawayBlock = true;
            err.runawayPath = path;
            err.runawayRemainingMs = remainingMs;
            return Promise.reject(err);
        }
        // Append timestamp, drop anything older than the window.
        const arr = _rate_hits.get(path) || [];
        arr.push(now);
        while (arr.length && arr[0] < now - RUNAWAY_WINDOW_MS) arr.shift();
        _rate_hits.set(path, arr);
        if (arr.length >= RUNAWAY_THRESHOLD) {
            const count = arr.length;
            const cooldownUntil = now + RUNAWAY_COOLDOWN_MS;
            _rate_blocked.set(path, cooldownUntil);
            _rate_hits.set(path, []); // wipe so the same evidence isn't re-counted
            // Loud log — visible without DevTools open.
            // eslint-disable-next-line no-console
            console.error(
                `[RunawayGuard] ${path} fired ${count}× in ${RUNAWAY_WINDOW_MS}ms ` +
                `— throttling for ${RUNAWAY_COOLDOWN_MS}ms. Likely a useEffect ` +
                `dep-array bug (object reference changes every render).`
            );
            try {
                window.dispatchEvent(new CustomEvent("shed:runaway", {
                    detail: {
                        endpoint: path,
                        count,
                        window_ms: RUNAWAY_WINDOW_MS,
                        cooldown_ms: RUNAWAY_COOLDOWN_MS,
                        cooldown_until: cooldownUntil,
                    },
                }));
            } catch (_) { /* no-op */ }
        }
    }
    return config;
});

api.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err.response && err.response.status === 401) {
            localStorage.removeItem("msd_token");
            localStorage.removeItem("msd_user");
            if (!window.location.pathname.startsWith("/login")) {
                window.location.href = "/login";
            }
        }
        // Budget-exceeded hint: upstream LLM failures surface as 502 with 'budget' in detail
        if (err.response && (err.response.status === 502 || err.response.status === 503)) {
            const msg = (err.response.data?.detail || "").toString().toLowerCase();
            if (msg.includes("budget") || msg.includes("credit") || msg.includes("quota")) {
                window.dispatchEvent(new CustomEvent("shed:budget-low", { detail: msg }));
            }
        }
        // Iter 31.139 (30 May 2026) — 402 from debit_credits carries a
        // structured detail. Surface the global CreditWallSheet instead
        // of a generic toast so the operator can top up in one tap.
        if (err.response && err.response.status === 402) {
            const d = err.response.data?.detail;
            if (d && typeof d === "object" && d.code === "INSUFFICIENT_CREDITS") {
                window.dispatchEvent(new CustomEvent("shed:credits-required", { detail: d }));
            }
        }
        return Promise.reject(err);
    }
);

export function saveSession(token, user) {
    localStorage.setItem("msd_token", token);
    // Iter 29 (Feb 2026): persist the full user record so LinkedIn-derived
    // fields (linkedin_picture, linkedin_sub, created_via, created_at) survive
    // a refresh and the Shell can render the avatar + new-arrival greeting.
    localStorage.setItem("msd_user", JSON.stringify(user || {}));
    // Iter 31.14 (23 May 2026): broadcast same-tab auth change so the
    // RadioProvider (and any other singleton listening) can re-evaluate
    // signed-in state without a full reload.
    try { window.dispatchEvent(new Event("shed:auth-change")); } catch (_) {}
}

export function getUser() {
    try {
        return JSON.parse(localStorage.getItem("msd_user") || "null");
    } catch {
        return null;
    }
}

// Bearer token accessor — used by direct fetch() paths that bypass axios
// (e.g. blob downloads where axios' default JSON transform gets in the way).
export function getToken() {
    return localStorage.getItem("msd_token");
}

export function logout() {
    localStorage.removeItem("msd_token");
    localStorage.removeItem("msd_user");
    window.location.href = "/login";
}
